import React, { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { AppContext } from 'src/components/app-context';

import { Panel, PanelHeader, PanelTitle } from '@prisjakt/ui/panel';
import { Column } from '@prisjakt/ui/list/variants';
import type { CategoryWithLogo } from 'src/components/category-list/types';
import { PageContent } from 'src/components/page';
import FeaturedProducts from 'src/components/featured-products';
import TrendingProducts from 'src/components/trending-products';
import CampaignDeals from '../../components/campaign-deals';
import PromoBanner from '../../components/campaign-banners/promo-banner';
import RecentlyVisited from '../../components/recently-visited';
import { CategoryList } from '../../components/category-list';
import AdContainer from '../../components/ads';
import { plainText } from '../editorial-content/components/slices/rich-text';
import CampaignBanner from './components/campaign-banner';
import ExpertSection from './components/experts';
import FeaturedArticle from './components/featured-article';
import ForumTopicListing from './components/forum-topic-listing';
import FallbackProductRecommendations from './components/personalized-recommendations/fallback-recommendations';
import PersonalizedProductRecommendations from './components/personalized-recommendations';
import PopularFacets from './components/popular-facets';
import { SearchHero } from './components/search-hero';
import SotyBanner from './components/soty-banner';
import { FrontPageBanner } from './components/front-page-banner';

import {
  useSotyState,
  SotyState,
} from './components/soty-banner/use-soty-state';

import type { AppState } from '../../reducers';
import { setSearchBarInHero } from '../search/actions/search';

import type {
  LandingPageQuery,
  Campaign,
  CampaignBanner as CampaignBannerType,
  Maybe,
  ExpertLandingPage,
} from '../../types';

import * as S from './styles';
import { useLandingPageQuery } from './queries/use-landing-page-query';
import Loader from 'src/components/loader';
import { useMatchedRoute } from 'src/hooks/use-matched-route';
import { NewProducts } from './components/new-products';

type Props = {
  data: LandingPageQuery;
};

type ActiveBanners = {
  active: (CampaignBannerType & { campaign: Campaign })[];
};

const selectActiveCampaign: (state: AppState) => Campaign = state =>
  state.globals.campaigns &&
  state.globals.campaigns.active.find(
    (c: Campaign) => c.type === 'OFFER' || c.type === 'SEARCH_BANNER'
  );

const selectBigCampaignBanner: (
  state: AppState
) => CampaignBannerType = state =>
  state.globals.campaignBanners &&
  state.globals.campaignBanners.active.find(
    (banner: CampaignBannerType) => banner.type === 'LANDING_PAGE_BIG'
  );

const selectCampaignBanners: (state: AppState) => ActiveBanners = state =>
  state.globals.campaignBanners;

const LandingPage = ({ data }: Props) => {
  const { t, market } = useContext(AppContext);
  const dispatch = useDispatch();
  const activeCampaign = useSelector(selectActiveCampaign);
  const bigCampaignBanner = useSelector(selectBigCampaignBanner);
  const campaignBanners = useSelector(selectCampaignBanners);
  const sotyState = useSotyState();

  const {
    campaignDeals,
    forumTopics,
    featuredProducts,
    featuredArticle,
    rootCategories,
    popularFacets,
    frontPageBanner,
  } = data;

  useEffect(() => {
    dispatch(setSearchBarInHero(true));

    // On unmount
    return () => {
      dispatch(setSearchBarInHero(false));
    };
  }, []);

  const categories: Maybe<CategoryWithLogo>[] = (rootCategories || [])
    .map(category =>
      category
        ? ({
            ...category,
            logo: { 192: category.logo },
          } as CategoryWithLogo)
        : null
    )
    .filter(x => x);

  const activeCampaignLogo =
    activeCampaign &&
    activeCampaign.banners &&
    activeCampaign.banners.find(
      banner => banner && banner.type.toUpperCase() === 'BANNER_LOGO_BIG'
    );

  const activeCampaignAsCategory: CategoryWithLogo | undefined =
    activeCampaign &&
    ({
      id: 'activeCampaign',
      name: activeCampaign.name,
      pathName: activeCampaign.web_uri && activeCampaign.web_uri.responsive,
      logo: {
        192: activeCampaignLogo ? activeCampaignLogo.imageUrl : null,
      },
    } as CategoryWithLogo);

  const beforeBanners =
    campaignBanners &&
    campaignBanners.active.filter(
      (b: CampaignBannerType) => b && b.type === 'BEFORE_PROMO_BANNER'
    );

  const categoriesWithActiveCampaign = activeCampaignAsCategory
    ? [activeCampaignAsCategory, ...categories]
    : categories;

  return (
    <S.LandingPage>
      <SearchHero />

      <S.Content>
        <AdContainer
          onlyWithServices={['appnexus']}
          placement="dubbelpanorama1"
          format="panorama_1"
          min={0}
          max={860}
        />
      </S.Content>

      <S.Content>
        <Column gap={1.5}>
          {beforeBanners && beforeBanners.length > 0 && (
            <PromoBanner inverted banners={beforeBanners} />
          )}

          <RecentlyVisited negativeMargin />

          {frontPageBanner ? (
            <FrontPageBanner
              buttonText={frontPageBanner.buttonText}
              image={frontPageBanner.image}
              link={frontPageBanner.link}
              subtitle={frontPageBanner.subtitle}
              title={frontPageBanner.title}
              buttonColor={frontPageBanner.buttonColor}
              textColor={frontPageBanner.textColor}
            />
          ) : null}

          <CampaignDeals deals={campaignDeals} negativeMargin />

          <FallbackProductRecommendations negativeMargin />

          <Panel
            containerProps={{
              'data-test': 'Panel',
              'data-test-type': 'categories',
            }}
          >
            <PanelHeader>
              <PanelTitle>{t('landing', 'categories.title')}</PanelTitle>
            </PanelHeader>
            <CategoryList
              displayLinkedCategories={false}
              categories={categoriesWithActiveCampaign}
            />
          </Panel>

          {bigCampaignBanner && (
            <CampaignBanner
              url={bigCampaignBanner.url}
              imageUrl={bigCampaignBanner.imageUrl}
              {...bigCampaignBanner.options}
            />
          )}

          {sotyState === SotyState.PROMO && <SotyBanner />}

          {market.experts.enabled && data.expertLandingPage && (
            <Panel>
              <ExpertSection
                data={data.expertLandingPage as ExpertLandingPage}
              />
            </Panel>
          )}

          {data.prismicArticles &&
            data.prismicArticles.nodes.length > 0 &&
            data.prismicArticles.nodes.map((article, index) => {
              const ArticleComponent = (
                <Panel key={article.id}>
                  <FeaturedArticle
                    pathName={article.pathName}
                    imageUrl={
                      article.data.featuredImage &&
                      article.data.featuredImage.url
                    }
                    title={plainText(article.data.title)}
                    preamble={plainText(article.data.metaDescription)}
                    linkText={t('landing', 'featured-article.read-more')}
                  />
                </Panel>
              );

              if (index === 0) {
                return (
                  <React.Fragment key="ad-container">
                    {ArticleComponent}
                    <AdContainer
                      placement="dubbelpanorama2"
                      format="panorama_2"
                      min={0}
                      max={860}
                      triggerPoint={100}
                    />
                  </React.Fragment>
                );
              }

              return ArticleComponent;
            })}

          {featuredArticle && (
            <Panel>
              <FeaturedArticle
                {...featuredArticle}
                linkText={t('landing', 'featured-article.read-more')}
              />
            </Panel>
          )}

          <PersonalizedProductRecommendations negativeMargin />

          <NewProducts />

          <TrendingProducts negativeMargin />

          <AdContainer
            placement="dubbelpanorama3"
            format="panorama_3"
            min={0}
            max={860}
            spacing={{ bottom: '20px' }}
            triggerPoint={100}
          />

          {featuredProducts &&
            featuredProducts.products &&
            featuredProducts.products.length > 0 && (
              <FeaturedProducts
                featuredProducts={featuredProducts}
                negativeMargin
              />
            )}

          {forumTopics && <ForumTopicListing topics={forumTopics} />}

          {popularFacets && <PopularFacets popularFacets={popularFacets} />}
        </Column>
      </S.Content>
    </S.LandingPage>
  );
};

const LandingPageLoader = () => {
  const route = useMatchedRoute();
  const landingPageQuery = useLandingPageQuery(route?.variables);

  const { data } = landingPageQuery;

  return (
    <PageContent withAds bottomPlacements={[]}>
      {landingPageQuery.isLoading ? <Loader /> : null}
      {data != null ? <LandingPage data={data} /> : null}
    </PageContent>
  );
};

export default LandingPageLoader;
